import { useState, useEffect, useRef } from "react";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import "../../../styles/pages/submissionTracker.css";
import { useNavigate, useLocation } from "react-router-dom";
import usePermission from "../../../shared/hooks/usePermission";


// ── Icons ─────────────────────────────────────────────────────────────────────
const IconDownload = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" x2="12" y1="15" y2="3" />
  </svg>
);

const IconUpload = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 16 12 12 8 16" />
    <line x1="12" x2="12" y1="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

const IconRefresh = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const IconTemplate = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" x2="8" y1="13" y2="13" />
    <line x1="16" x2="8" y1="17" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

// ── Step bar ──────────────────────────────────────────────────────────────────
const Steps = ({ active }) => {
  const steps = [
    
    { num: 1, label: "Download Template" },
    { num: 2, label: "Upload File" },
    { num: 3, label: "Validate" },
    { num: 4, label: "Import" },
  ];
  return (
    <div className="iad-steps">
      {steps.map((s, i) => (
        <div key={s.num} className={`iad-step${active >= s.num ? " iad-step--active" : ""}`}>
          <div className="iad-step__bubble">{s.num}</div>
          <span className="iad-step__label">{s.label}</span>
          {i < steps.length - 1 && <div className="iad-step__line" />}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const Import = () => {
  const { hasPermission } = usePermission();
const [activeTab, setActiveTab] = useState("import");
const canCreate =
  hasPermission(
    "import_data",
    "import"
  );
  const fileInputRef = useRef(null);
  const { reportingYear, reportingYearId } = useReportingYear();
  const navigate = useNavigate();
  const location = useLocation();

  const [previewCols, setPreviewCols] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);

  const [file,         setFile]         = useState(null);
  const [dragOver,     setDragOver]     = useState(false);
  const [importing,    setImporting]    = useState(false);
  const [importError,  setImportError]  = useState("");


  const [legalEntities,  setLegalEntities]  = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(location.state?.returnState?.selectedEntity ?? "");

  // ── Clear router state after reading it so refresh doesn't re-apply ──────
  useEffect(() => {
    if (location.state?.returnState) {
      window.history.replaceState({}, "");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps



  // ── Fetch activity types ────────────────────────────────────────────────


  // ── Fetch legal entities ────────────────────────────────────────────────
  useEffect(() => {
    if (!reportingYearId) return;
    apiClient
      .get("/api/legal-entities-by-year", { params: { reporting_year_id: reportingYearId } })
      .then((res) => setLegalEntities(res.data.code === 200 ? res.data.data || [] : []))
      .catch(() => setLegalEntities([]));
  }, [reportingYearId]);

  

  // ── Download cached template blob ───────────────────────────────────────
const handleDownloadTemplate=async()=>{
  try{
    const {data}=await apiClient.get("/api/activity/download-master-template",{responseType:"blob"});
    const url=URL.createObjectURL(new Blob([data]));
    const link=document.createElement("a");
    link.href=url;
    link.download = `Activity_Import_Template_${reportingYear}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }catch(err){
    console.error(err);
    setImportError("Unable to download template.");
  }
};

  // ── Parse uploaded CSV for preview ────────────────────────────────────
  const parseUploadedCSV = (uploadedFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const lines = e.target.result.split(/\r?\n/).filter((l) => l.trim() !== "");
        if (lines.length === 0) return;

        // Parse header — strip trailing comma if present
        const rawHeader = lines[0].replace(/,$/, "");
        const headers   = rawHeader.split(",").map((c) => c.replace(/['"]+/g, "").trim());

        // Parse ALL data rows (no slice limit), strip trailing commas
        const dataRows = lines.slice(1).map((row) => {
          const cleaned = row.replace(/,$/, ""); // remove trailing comma
          return cleaned.split(",").map((c) => c.replace(/['"]+/g, "").trim());
        });

        setPreviewCols(headers);
        setPreviewRows(dataRows);
      } catch {
        setPreviewCols([]);
        setPreviewRows([]);
      }
    };
    reader.readAsText(uploadedFile);
  };

  // ── File handling ──────────────────────────────────────────────────────
  const handleFileSelect = (f) => {
    if (!f) return;
    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (ext !== ".csv") {
      setImportError("Only .csv files are supported.");
      return;
    }
    setFile(f);
    setImportError("");
    parseUploadedCSV(f);
  };

  const handleDrop      = (e) => { e.preventDefault(); setDragOver(false); handleFileSelect(e.dataTransfer.files?.[0]); };
  const handleFileInput = (e) => handleFileSelect(e.target.files?.[0]);
  const removeFile      = () => {
    setFile(null);
    setPreviewCols([]);
    setPreviewRows([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Validate & Import ──────────────────────────────────────────────────
  // API: POST /api/activity/upload-template
  // Body (form-data): activity_type_id, reporting_year_id, legal_entity_id, file
  const handleImport = async () => {
    

    // Validate legal entity is selected before submitting
    if (!selectedEntity) {
      setImportError("Please select a Legal Entity before importing.");
      return;
    }

    setImporting(true);
    setImportError("");

    try {
      const fd = new FormData();
      fd.append("reporting_year_id", reportingYearId ?? "");
      fd.append("legal_entity_id",   selectedEntity);   // ← required by API
      fd.append("file",              file);

      const res = await apiClient.post("/api/activity/upload-template", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.success) {
        navigate("/dashboard/import-summary", {
          state: {
            importResult: {
              ...res.data,
              filename: file.name,
            },
            // ← save current selections so we can restore on back
            returnState: {
              selectedEntity
            },
          },
        });
      } else {
        setImportError(res.data?.message || "Import failed. Please check your file and try again.");
      }
    } catch (err) {
      setImportError(
        err.response?.data?.message || err.message || "Import failed. Please try again."
      );
    } finally {
      setImporting(false);
    }
  };

  // ── Activity change ────────────────────────────────────────────────────
  

if (!canCreate) {
  return (
    <div className="dash-page">
      <div className="empty-entity">
        <div className="empty-entity__icon">
          <i className="fa-solid fa-lock" />
        </div>

        <div className="empty-entity__title">
          Access Restricted
        </div>

        <div className="empty-entity__sub">
          You do not have permission to import activities.
        </div>
      </div>
    </div>
  );
}
  // Button enables as soon as activity + file are ready.
  // Legal entity is validated at submit time (shows inline error if missing).
  const canImport =
    reportingYearId &&
    selectedEntity &&
    file &&
    !importing;

  return (
    <div className="dash-page">
      {/* Page header */}
     
      <div className="iad-page-header">
    <div className="dash-page-subtitle">
        ENVIRONMENT & CARBON
    </div>

    <div className="dash-page-title">
        Import Activity Data
    </div>

    <div className="dash-page-subtitle">
        Bulk-upload activity data using the templates below.
    </div>

    <div className="import-tabs">

        <button
            className={`import-tab ${activeTab === "import" ? "active" : ""}`}
            onClick={() => setActiveTab("import")}
        >
            <i className="fa-solid fa-file-import"></i>
            Import Data
        </button>

        <button
            className={`import-tab ${activeTab === "reference" ? "active" : ""}`}
            onClick={() => navigate("/dashboard/import/reference")}
        >
            <i className="fa-solid fa-book"></i>
            Reference Values
        </button>

    </div>

</div>

      {/* Filter card */}
      <div className="filter-card">
        <div className="filter-group">
          <label>Reporting Year *</label>
          <input className="filter-input" value={reportingYear || ""} disabled />
        </div>
        <div className="filter-group">
          <label>Legal Entity *</label>
          <select
            className="filter-input"
            value={selectedEntity}
            onChange={(e) => setSelectedEntity(e.target.value)}
          >
            <option value="">Select</option>
            {legalEntities.map((entity) => (
              <option key={entity.entity_id} value={entity.entity_id}>
                {entity.entity_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Step bar */}
     

      {/* 2×2 grid */}
      <div className="iad-grid">

        {/* Card 1 — Select Activity */}
        

        {/* Card 2 — Download Template */}
        <div className="le-card iad-card">
          <div className="iad-card__step-label">
            <span className="iad-step-num">1</span>
            Download Template
          </div>
          <p className="iad-card__desc">
            Download the master import template.
            The template supports all configured activities.
          </p>
          <button
            className="iad-btn-download"
            onClick={handleDownloadTemplate}
            disabled={false}
          >
            <IconDownload />
           Download Import Template
          </button>
        </div>

        {/* Card 3 — Upload File */}
        <div className="le-card iad-card">
          <div className="iad-card__step-label">
            <span className="iad-step-num">2</span>
            Upload File
          </div>
          {!file ? (
            <div
    className={`iad-dropzone${dragOver ? " iad-dropzone--over" : ""}`}
    onClick={() => fileInputRef.current?.click()}
    onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
    }}
    onDragLeave={() => setDragOver(false)}
    onDrop={handleDrop}
>
              <div className="iad-dropzone__icon"><IconUpload /></div>
              <div className="iad-dropzone__title">Drag &amp; drop your file here</div>
              <div className="iad-dropzone__sub">or click to browse</div>
              <div className="iad-dropzone__hint">SUPPORTED: .CSV ONLY</div>
              <div className="iad-dropzone__hint">Download the master template, fill it with activity data and upload it here.</div>
            </div>
          ) : (
            <div className="iad-file-chip">
              <div className="iad-file-chip__info">
                <span className="iad-file-chip__icon">📎</span>
                <span className="iad-file-chip__name">{file.name}</span>
                <span className="iad-file-chip__size">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
              <button className="iad-file-chip__remove" onClick={removeFile}>✕</button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="iad-file-input"
            onChange={handleFileInput}
          />
        </div>

        {/* Card 4 — Import */}
          <div className="le-card iad-card iad-card--import">
          <div className="iad-card__step-label">
            <span className="iad-step-num">3</span>
            Import
          </div>
          <p className="iad-card__desc">
            We'll validate every row before importing. You can review errors and proceed with valid records.
          </p>

          {/* ── Custom Validation Rules UI ────────────────────────────────────── */}
          <div 
            className="iad-import-instructions"
          >
            <div>⚠️ CSV Import Guidelines:</div>
<ul>
  <li>Use the provided CSV template without modifying its column structure. Do not add, remove, rename, or reorder any columns.</li>
  <li>The CSV file must contain only one worksheet. Files with multiple worksheets will not be processed.</li>
  <li>Each CSV file must contain data for only one Legal Entity. Do not combine locations from multiple Legal Entities.</li>
  <li>Activity Name must exactly match the corresponding Reference Values.</li>
  <li>Department is optional.</li>
  <li>Date format must be <strong>dd/mm/yyyy</strong> or <strong>dd-mm-yyyy</strong>.</li>
  <li>Value must be numeric (e.g., <strong>1234</strong> or <strong>1234.50</strong>). Thousand separators (e.g., <strong>1,234</strong>) are not supported.</li>
  <li>Unit is determined automatically based on the selected Activity.</li>
</ul>
</div>

          {importError && (
            <div className="iad-result iad-result--error">✕ {importError}</div>
          )}

          <button
            className={`iad-btn-import${canImport ? "" : " iad-btn-import--disabled"}`}
            onClick={handleImport}
            disabled={!canImport}
          >
            {importing ? (
              <><span className="iad-spinner" />Importing…</>
            ) : (
              <><IconRefresh />Validate &amp; Import</>
            )}
          </button>
        </div>

      </div>

      {/* Uploaded file preview */}
      {file && previewCols.length > 0 && (
        <div className="le-card iad-preview-card">
          <div className="iad-preview-header">
            <div className="iad-preview-header__left">
              <IconTemplate />
              <span>Uploaded File Preview — {file.name}</span>
            </div>
            <span className="iad-preview-badge">{previewCols.length} columns detected</span>
          </div>
          <div className="iad-preview-table-wrap">
            <table className="iad-preview-table">
              <thead>
                <tr>
                  {previewCols.map((col, idx) => (
                    <th key={idx}>{col.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.length === 0 ? (
                  <tr>
                    {previewCols.map((_, ci) => (
                      <td key={ci} className="td-muted">—</td>
                    ))}
                  </tr>
                ) : (
                  previewRows.map((row, ri) => (
                    <tr key={ri}>
                      {previewCols.map((_, ci) => {
                        const val = row[ci];
                        // Show "—" only for genuinely empty/missing cells
                        const isEmpty = val === undefined || val === "" || val === null;
                        return <td key={ci}>{isEmpty ? "—" : val}</td>;
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Import;